/*
 * Copyright © 2024-present Mia s.r.l.
 * All rights reserved
 */

import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import config from '../config';
import './PreviewLogs.css';

/**
 * PreviewLogs Component
 * Displays real-time logs from the preview container
 */
const PreviewLogs = ({sessionId, enabled = true, position = 'bottom', containerPort = 3001, onAskAI, isExpanded: externalIsExpanded, onToggleExpanded, onErrorCountChange, onBuildError, hideUI = false}) => {
  const [logs, setLogs] = useState([]);
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  
  // Use external control if provided, otherwise use internal state
  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;
  const setIsExpanded = onToggleExpanded || setInternalIsExpanded;
  const [isConnected, setIsConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'stdout', 'stderr', 'errors', 'client'
  // buildStatus received in the initial SSE payload from the server.
  // 'completed' means the last build succeeded; anything else means errors may be active.
  const [historicalBuildStatus, setHistoricalBuildStatus] = useState(null);
  const logsContainerRef = useRef(null);
  const eventSourceRef = useRef(null);
  const buildErrorFiredRef = useRef(false);
  // Keep stable refs so we never need onBuildError / logs in effect deps
  const onBuildErrorRef = useRef(onBuildError);
  const latestLogsRef = useRef(logs);

  // Keep refs in sync with latest prop/state values on every render
  useEffect(() => {
    onBuildErrorRef.current = onBuildError;
  });
  useEffect(() => {
    latestLogsRef.current = logs;
  });

  // Reset the "already fired" flag and historical build status whenever a new preview session starts
  useEffect(() => {
    buildErrorFiredRef.current = false;
    setHistoricalBuildStatus(null);
  }, [sessionId]);

  // Scroll to bottom when new logs arrive and autoScroll is enabled
  useEffect(() => {
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Listen for client-side errors from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Only accept messages from our iframe origin
      if (!event.data || event.data.source !== 'mia-flow-preview-client') {
        return;
      }

      const {type, message, stack, timestamp} = event.data;
      
      // Add client error to logs
      const logEntry = {
        type: 'client-error',
        line: stack || message,
        timestamp: timestamp || new Date().toISOString(),
      };

      setLogs(prev => [...prev, logEntry]);
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Connect to log stream
  const connectToStream = useCallback(() => {
    if (!sessionId || !enabled) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Detect if running in production
      const isProduction = typeof window !== 'undefined' && 
                          window.location.origin && 
                          !window.location.origin.includes('localhost') && 
                          !window.location.origin.includes('127.0.0.1');

      const containerMap = {
        3001: 'react-container-live',
        3002: 'nextjs-container-live',
        3003: 'reactnative-container-live',
        3004: 'angular-container-live',
        3005: 'vue-container-live',
        3006: 'jquery-container-live',
        3007: 'aspnet-frontend-container-live',
        3008: 'docusaurus-container-live',
        3011: 'node-container-live',
        3012: 'go-container-live',
        3013: 'python-container-live',
        3014: 'java-container-live',
        3015: 'dotnet-container-live',
        3016: 'rust-container-live',
        3017: 'php-container-live',
        3018: 'kotlin-container-live',
        3019: 'cpp-container-live',
        3020: 'qsharp-container-live',
        3021: 'elixir-container-live',
        3030: 'crud-service-universal',
      };

      const containerName = containerMap[containerPort] || 'react-container-live';

      // Build the appropriate URL
      let streamUrl;
      if (isProduction) {
        // React container uses direct public URL
        const publicBaseUrl = config.publicBaseUrl || 'http://localhost:3001';
        streamUrl = `${publicBaseUrl}/api/preview/${containerName}/api/preview/${sessionId}/logs/stream`;
      } else {
        // Development or other containers use localhost
        streamUrl = `/${containerName}/api/preview/${sessionId}/logs/stream`;
      }
      
      const eventSource = new EventSource(streamUrl);

      eventSource.onopen = () => {
        // eslint-disable-next-line no-console
        console.log('📡 Connected to log stream');
        setIsConnected(true);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'initial') {
            // Initial logs are buffered history — we don't have per-line timestamps.
            // Mark them as historical so they render as "history" instead of a fake time.
            // The server also sends buildStatus so we know if these errors are already resolved.
            setHistoricalBuildStatus(data.buildStatus || null);
            const initialLogs = [
              ...data.stdout.map(line => ({
                type: 'stdout',
                line,
                isHistorical: true,
                timestamp: null,
              })),
              ...data.stderr.map(line => ({
                type: 'stderr',
                line,
                isHistorical: true,
                timestamp: null,
              })),
            ];
            setLogs(initialLogs);
          } else {
            // New log line received
            setLogs(prev => [...prev, data]);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error parsing log message:', err);
        }
      };

      eventSource.onerror = (error) => {
        // Suppress verbose error logging
        // console.error('❌ Log stream error:', error);
        setIsConnected(false);
        eventSource.close();
        
        // Retry connection after 5 seconds
        setTimeout(() => {
          if (enabled) {
            connectToStream();
          }
        }, 5000);
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to connect to log stream:', error);
      setIsConnected(false);
    }
  }, [sessionId, enabled, containerPort]);

  // Connect/disconnect from stream
  useEffect(() => {
    if (enabled) {
      connectToStream();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId, containerPort]);
  // Note: We include sessionId and containerPort directly instead of connectToStream
  // to avoid the function reference changing and causing unnecessary reconnections

  // Clear logs
  const handleClearLogs = () => {
    setLogs([]);
  };

  // Toggle auto-scroll
  const handleToggleAutoScroll = () => {
    setAutoScroll(prev => !prev);
  };

  // Send logs to AI for help
  const handleAskAI = () => {
    if (!onAskAI || logs.length === 0) return;
    
    // Format logs for AI
    const logText = logs
      .map(log => `[${formatTime(log.timestamp)}] [${log.type}] ${stripAnsi(log.line)}`)
      .join('\n');
    
    // Create prompt with text language specifier for proper code block rendering
    const prompt = `I'm getting errors in my preview. Please help me fix these issues:\n\n\`\`\`text\n${logText}\n\`\`\`\n\nPlease analyze the errors and provide a solution.`;
    
    onAskAI(prompt);
  };

  // Filter logs based on selected filter
  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    if (filter === 'stdout') return log.type === 'stdout';
    if (filter === 'stderr') return log.type === 'stderr';
    if (filter === 'client') return log.type === 'client-error';
    if (filter === 'errors') {
      // Include both server-side and client-side errors
      if (log.type === 'client-error') return true;
      // Simple error detection - contains 'error', 'failed', etc.
      const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
      return errorPatterns.test(log.line);
    }
    return true;
  });

  // Detect if a log line is a warning (starts with 'warn' prefix)
  const isWarningLine = (line) => /^\s*warn[\s\-:]/i.test(line || '');

  // Count errors for collapsed bar indicator (warnings excluded)
  const errorCount = logs.filter(log => {
    if (log.type === 'client-error') return true;
    if (isWarningLine(log.line)) return false; // warnings are not errors
    // Detect error patterns in log lines
    const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
    return errorPatterns.test(log.line);
  }).length;

  // Count warnings separately
  const warningCount = logs.filter(log => {
    if (log.type === 'client-error') return false;
    return isWarningLine(log.line);
  }).length;

  // Detect build-success lines (Next.js / other frameworks)
  const isBuildSuccessLine = (line) => {
    if (!line) return false;
    return /✓\s*Ready|compiled (client and server )?successfully|\bReady in\b|ready started server|\bReady\b.*started/i.test(line);
  };

  // Find the index of the last build-success log entry
  const lastSuccessIndex = logs.reduce((acc, log, idx) => {
    if (log.type !== 'client-error' && isBuildSuccessLine(log.line)) return idx;
    return acc;
  }, -1);

  // Active errors = errors still unresolved by a successful build.
  // Whether historical errors count as active depends on what the server reports:
  //   buildStatus 'completed' → last build succeeded, historical errors are resolved
  //   anything else (failed / null / building) → historical errors are still active
  const historicalBuildSucceeded = historicalBuildStatus === 'completed';

  const activeErrorCount = logs.filter((log, idx) => {
    if (log.isHistorical) {
      if (historicalBuildSucceeded) return false; // resolved by a later successful build
      if (log.type === 'client-error') return true;
      if (isWarningLine(log.line)) return false;
      const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
      return errorPatterns.test(log.line);
    }
    if (idx <= lastSuccessIndex) return false;
    if (log.type === 'client-error') return true;
    if (isWarningLine(log.line)) return false;
    const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
    return errorPatterns.test(log.line);
  }).length;

  const staleErrorCount = logs.filter((log, idx) => {
    if (log.isHistorical) {
      if (!historicalBuildSucceeded) return false; // still active, not stale
      if (log.type === 'client-error') return true;
      if (isWarningLine(log.line)) return false;
      const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
      return errorPatterns.test(log.line);
    }
    if (lastSuccessIndex < 0) return false;
    if (idx > lastSuccessIndex) return false;
    if (log.type === 'client-error') return true;
    if (isWarningLine(log.line)) return false;
    const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
    return errorPatterns.test(log.line);
  }).length;

  // Notify parent of error count changes and fire onBuildError once per session
  useEffect(() => {
    if (onErrorCountChange) {
      onErrorCountChange(activeErrorCount);
    }

    // Fire onBuildError the first time errors appear in this session.
    // We intentionally read onBuildError and logs through refs so that
    // a Canvas.jsx re-render (e.g. caused by closing the modal) does NOT
    // add onBuildError/logs to this effect's deps and cannot immediately
    // reopen the modal right after the user has dismissed it.
    if (activeErrorCount > 0 && !buildErrorFiredRef.current && onBuildErrorRef.current) {
      buildErrorFiredRef.current = true;

      // Collect error log lines (strip ANSI codes, skip warnings)
      // eslint-disable-next-line no-control-regex
      const ansiRegex = /\x1B\[[0-9;]*[A-Za-z]/g;
      const errorLines = latestLogsRef.current
        .filter(log => {
          if (log.type === 'client-error') return true;
          if (!log.line) return false;
          if (/^\s*warn[\s\-:]/i.test(log.line)) return false;
          return /error|failed|exception|undefined|cannot|unexpected/i.test(log.line);
        })
        .map(log => (log.line || '').replace(ansiRegex, ''))
        .slice(0, 40)
        .join('\n');

      onBuildErrorRef.current(errorLines || 'Errors detected in preview logs.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeErrorCount, onErrorCountChange]); // onBuildError and logs are accessed via refs on purpose

  // Strip ANSI color codes from log lines
  const stripAnsi = (str) => {
    if (!str) return '';
    // eslint-disable-next-line no-control-regex
    return str.replace(/\x1B\[[0-9;]*[A-Za-z]/g, '');
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!enabled || !sessionId || hideUI) {
    return null;
  }

  return (
    <div className={`preview-logs preview-logs-${position} ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Header bar - clickable to toggle expansion */}
      <div 
        className="preview-logs-header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="preview-logs-title">
          <span className="preview-logs-icon">{'📋'}</span>
          <span>{'Preview Logs'}</span>
          {isConnected && <span className="preview-logs-status connected">{'●'}</span>}
          {!isConnected && <span className="preview-logs-status disconnected">{'●'}</span>}
          <span className="preview-logs-count">{'('}{filteredLogs.length}{')'}</span>
          {activeErrorCount > 0 && (
            <span className="preview-logs-error-badge" title={`${activeErrorCount} active error${activeErrorCount !== 1 ? 's' : ''} detected`}>
              {'⚠️ '}{activeErrorCount}{' error'}{activeErrorCount !== 1 ? 's' : ''}
            </span>
          )}
          {activeErrorCount === 0 && staleErrorCount > 0 && (
            <span className="preview-logs-resolved-badge" title={`${staleErrorCount} error${staleErrorCount !== 1 ? 's' : ''} resolved — build succeeded`}>
              {'✓ '}{staleErrorCount}{' resolved'}
            </span>
          )}
          {warningCount > 0 && (
            <span className="preview-logs-warning-badge" title={`${warningCount} warning${warningCount !== 1 ? 's' : ''}`}>
              {'⚠ '}{warningCount}{' warning'}{warningCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="preview-logs-actions" onClick={(e) => e.stopPropagation()}>
          {isExpanded && (
            <>
              {onAskAI && logs.length > 0 && (
                <button
                  className="preview-logs-btn preview-logs-ask-ai"
                  onClick={handleAskAI}
                  title="Ask AI to fix errors"
                  type="button"
                >
                  {'🤖 Ask AI'}
                </button>
              )}
              
              <select
                className="preview-logs-filter"
                onChange={(e) => setFilter(e.target.value)}
                value={filter}
              >
                <option value="all">{'All'}</option>
                <option value="stdout">{'stdout'}</option>
                <option value="stderr">{'stderr'}</option>
                <option value="client">{'Client Errors'}</option>
                <option value="errors">{'All Errors'}</option>
              </select>
              
              <button
                className={`preview-logs-btn preview-logs-icon-btn ${autoScroll ? 'active' : ''}`}
                onClick={handleToggleAutoScroll}
                title={autoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
                type="button"
              >
                {/* Scroll-to-bottom icon */}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3v14"/>
                  <path d="M5 14l7 7 7-7"/>
                  <path d="M5 21h14"/>
                </svg>
              </button>
              
              <button
                className="preview-logs-btn preview-logs-icon-btn"
                onClick={handleClearLogs}
                title="Clear logs"
                type="button"
              >
                {/* Trash icon */}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </>
          )}
          
          <button
            className="preview-logs-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            title={isExpanded ? 'Collapse logs' : 'Expand logs'}
            type="button"
          >
            {isExpanded ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {/* Logs content */}
      {isExpanded && (
        <div className="preview-logs-content" ref={logsContainerRef}>
          {filteredLogs.length === 0 ? (
            <div className="preview-logs-empty">
              {isConnected ? 'Waiting for logs...' : 'Connecting to log stream...'}
            </div>
          ) : (
            <div className="preview-logs-lines">
              {filteredLogs.map((log, index) => (
                <div
                  className={`preview-log-line preview-log-${log.type}${log.isHistorical ? ' preview-log-historical' : ''}`}
                  key={index}
                >
                  <span className="preview-log-time">
                    {log.isHistorical ? 'history' : formatTime(log.timestamp)}
                  </span>
                  <span className="preview-log-type-indicator">
                    {log.type === 'stderr' ? '⚠' : log.type === 'client-error' ? '🔴' : '•'}
                  </span>
                  <span className="preview-log-text">{stripAnsi(log.line)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PreviewLogs.propTypes = {
  containerPort: PropTypes.number,
  enabled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onAskAI: PropTypes.func,
  onBuildError: PropTypes.func,
  onErrorCountChange: PropTypes.func,
  onToggleExpanded: PropTypes.func,
  position: PropTypes.oneOf(['top', 'bottom']),
  sessionId: PropTypes.string,
};

export default PreviewLogs;
